import { useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import type { Operation, OperationStatus } from "../types/operation";
import { BatchDetails, BatchSummary } from "./BatchDetails";
import { useOperation } from "../hooks/useOperation";

const STATUS_ICON: Record<OperationStatus, string> = {
  running: "▶",
  done: "✓",
  failed: "✗",
  rejected: "⊘",
  stale: "⚠",
  unknown: "○",
};

const STATUS_COLOR: Record<OperationStatus, string> = {
  running: "info.main",
  done: "success.main",
  failed: "error.main",
  rejected: "text.secondary",
  stale: "warning.main",
  unknown: "text.secondary",
};

const STATUS_LABEL: Record<OperationStatus, string> = {
  running: "Em execução",
  done: "Concluído",
  failed: "Falhou",
  rejected: "Rejeitado",
  stale: "Sem atividade",
  unknown: "Desconhecido",
};

const PHASE_LABEL: Record<string, string> = {
  locking: "Aguardando lock",
  metadata: "Coletando metadados",
  downloading: "Download",
  scanning: "Insert",
  finalizing: "Finalizando",
  done: "Concluído",
  failed: "Falhou",
};

// Janela sem updatedAt antes de mostrar "Sem atividade" — 120s = metade do
// threshold server-side (900s), dá tempo suficiente pro backend responder
// antes do usuário se assustar.
const STALE_THRESHOLD_MS = 120_000;

// codes da operação failed em que o usuário só precisa tentar de novo depois:
// hora do ticket em limpeza, ou reidratada por outra operação.
const RETRY_LATER_MSG: Record<string, string> = {
  cleanup_in_progress: "Limpeza em andamento para este ticket, tente em alguns minutos",
  hour_in_use: "Outra reidratação deste ticket está em andamento, tente em alguns minutos",
};

function shortId(id: string): string {
  return id.length <= 8 ? id : id.slice(0, 8);
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// formatStart: "hoje 21:00", "ontem 21:00" ou "30/09 21:00" — só a hora não
// diz de que dia é a operação no histórico.
function formatStart(iso: string, now: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--:--";
  const time = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return `hoje ${time}`;
  if (sameDay(d, yesterday)) return `ontem ${time}`;
  return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${time}`;
}

function formatFull(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatDuration(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

function useTicker(enabled: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      window.clearInterval(id);
    };
  }, [enabled, intervalMs]);
  return now;
}

// formatBytes segue o formato do backend (1 casa, KB/MB/GB em base 1024).
function formatBytes(b: number): string {
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;
  if (b >= GB) return `${(b / GB).toFixed(1)} GB`;
  if (b >= MB) return `${(b / MB).toFixed(1)} MB`;
  if (b >= KB) return `${(b / KB).toFixed(1)} KB`;
  return `${b} B`;
}

function ratioPct(part: number, total: number): number {
  return Math.min(100, Math.floor((part * 100) / total));
}

function extractEventsRehydrated(op: Operation): number | null {
  if (typeof op.eventsRehydrated === "number") return op.eventsRehydrated;
  if (!op.result || typeof op.result !== "object") return null;
  const candidate = op.result as Record<string, unknown>;
  if (typeof candidate.eventsRehydrated === "number") {
    return candidate.eventsRehydrated;
  }
  const summary = candidate.summary;
  if (summary && typeof summary === "object") {
    const s = summary as Record<string, unknown>;
    if (typeof s.totalEventsRehydrated === "number") {
      return s.totalEventsRehydrated;
    }
  }
  return null;
}

// deriveDisplayStatus converte o status "cru" do server (running/done/failed)
// pro status efetivo da UI (podendo marcar "stale" se updatedAt muito velho).
function deriveDisplayStatus(op: Operation, now: number): OperationStatus {
  if (op.status !== "running") return op.status;
  const updatedMs = Date.parse(op.updatedAt);
  if (Number.isNaN(updatedMs)) return op.status;
  if (now - updatedMs > STALE_THRESHOLD_MS) return "stale";
  return "running";
}

type OperationItemProps = {
  op: Operation;
  /** Quando false, desliga o polling de 1s — item ainda renderiza via
   *  snapshot da lista. Default true por back-compat. OperationsList usa
   *  false nos items além do cap de fan-out (ver MAX_POLL_RUNNING). */
  enablePolling?: boolean;
};

export function OperationItem({ op: listOp, enablePolling = true }: OperationItemProps) {
  const [showBatch, setShowBatch] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  // Polling de 1s só pra ops running E quando o consumer autoriza (cap de
  // fan-out). Usa o valor "fresco" do server pra atualizar em tempo real;
  // se o server ainda não tem ou polling desligado, cai no snapshot da
  // lista (listOp) como fallback.
  const detailQuery = useOperation(
    listOp.trackingId,
    enablePolling && listOp.status === "running",
  );
  // Quando a lista (5s polling) já reporta estado terminal, ela é fonte
  // de verdade. O detail (1s polling) é desabilitado nesse instante, mas
  // detailQuery.data fica com o último snapshot running cacheado — usá-lo
  // mascararia o terminal e disparou "Sem atividade" via deriveDisplayStatus
  // (race entre list update e cancelamento da query do detail).
  const op: Operation =
    listOp.status === "running" ? (detailQuery.data ?? listOp) : listOp;

  const isActive = op.status === "running";
  const now = useTicker(isActive);

  const displayStatus = deriveDisplayStatus(op, now);

  const startedAtMs = Date.parse(op.startedAt);
  const finishedAtMs = op.finishedAt ? Date.parse(op.finishedAt) : null;

  const elapsedMs = isActive
    ? now - startedAtMs
    : finishedAtMs !== null
      ? finishedAtMs - startedAtMs
      : null;

  const statusColor = STATUS_COLOR[displayStatus];
  const statusIcon = STATUS_ICON[displayStatus];
  const statusLabel = STATUS_LABEL[displayStatus];

  const staleSeconds = (() => {
    if (displayStatus !== "stale") return null;
    const diff = now - Date.parse(op.updatedAt);
    if (Number.isNaN(diff) || diff < 0) return null;
    return Math.floor(diff / 1000);
  })();

  const eventsRehydrated =
    displayStatus === "done" ? extractEventsRehydrated(op) : null;

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 2 }}
      role="listitem"
      aria-label={`Operação ${shortId(op.trackingId)} - ${statusLabel}`}
    >
      <CardContent sx={{ pb: "16px !important" }}>
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                component="span"
                sx={{ color: statusColor, fontWeight: 700, fontSize: 18 }}
                aria-hidden="true"
              >
                {statusIcon}
              </Typography>
              <Tooltip
                title={`Início: ${formatFull(op.startedAt)} · Fim: ${
                  op.finishedAt ? formatFull(op.finishedAt) : "em andamento"
                }`}
                arrow
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  {formatStart(op.startedAt, now)}
                </Typography>
              </Tooltip>
              <TrackingIdCopy trackingId={op.trackingId} />
              <Typography variant="body2" color="text.secondary">
                · {op.kind === "single" ? "single" : `batch (${op.ticketIds.length})`}
              </Typography>
              {op.createdBy ? (
                <Typography variant="body2" color="text.secondary">
                  · {op.createdBy}
                </Typography>
              ) : null}
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: statusColor, fontWeight: 600 }}
            >
              {statusLabel}
            </Typography>
          </Stack>

          <TicketSummary op={op} />

          <PhaseProgressBars op={op} shortTrackingId={shortId(op.trackingId)} />

          {op.kind === "single" && op.single ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {op.single.eventsInserted.toLocaleString("pt-BR")}
              {op.single.totalExpected > 0
                ? ` / ${op.single.totalExpected.toLocaleString("pt-BR")}`
                : ""}{" "}
              eventos
              {op.single.totalExpected > 0
                ? ` (${Math.min(
                    100,
                    Math.floor(
                      (op.single.eventsInserted * 100) / op.single.totalExpected,
                    ),
                  )}%)`
                : ""}
            </Typography>
          ) : null}

          {op.kind === "batch" && op.batch ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <BatchSummary tickets={op.batch.tickets} />
              <Button
                size="small"
                variant="text"
                onClick={() => setShowBatch((v) => !v)}
                aria-expanded={showBatch}
                sx={{ textTransform: "none" }}
              >
                {showBatch ? "▲ ocultar tickets" : "▼ ver tickets"}
              </Button>
            </Stack>
          ) : null}

          {op.kind === "batch" ? (
            <BatchDetails open={showBatch} batch={op.batch} />
          ) : null}

          {displayStatus === "stale" ? (
            <Alert severity="warning" role="alert">
              Sem atividade
              {staleSeconds !== null ? ` há ${formatDuration(staleSeconds * 1000)}` : ""}
              . Aguardando o backend voltar a progredir.
            </Alert>
          ) : null}

          {displayStatus === "failed" ? (
            <Stack spacing={0.5}>
              {op.code && RETRY_LATER_MSG[op.code] ? (
                <Alert severity="warning" role="alert">
                  {RETRY_LATER_MSG[op.code]}
                </Alert>
              ) : (
                <Alert severity="error" role="alert">
                  {op.error || "Falha na reidratação"}
                </Alert>
              )}
              <Box>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowErrorDetails((v) => !v)}
                  aria-expanded={showErrorDetails}
                  sx={{ textTransform: "none" }}
                >
                  {showErrorDetails ? "▲ ocultar detalhes" : "▼ ver detalhes"}
                </Button>
                <Collapse in={showErrorDetails} unmountOnExit>
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      fontSize: 12,
                      maxHeight: 240,
                      overflow: "auto",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {JSON.stringify(
                      {
                        error: op.error,
                        code: op.code,
                        phase: op.phase,
                        pct: op.pct,
                        msg: op.msg,
                        single: op.single,
                        batch: op.batch,
                      },
                      null,
                      2,
                    )}
                  </Box>
                </Collapse>
              </Box>
            </Stack>
          ) : null}

          {displayStatus === "done" ? (
            <Typography variant="body2" color="text.secondary">
              {eventsRehydrated !== null
                ? `${eventsRehydrated.toLocaleString("pt-BR")} eventos`
                : "concluído"}
              {elapsedMs !== null ? ` · ${formatDuration(elapsedMs)}` : ""}
            </Typography>
          ) : null}

          {isActive && elapsedMs !== null ? (
            <Typography variant="caption" color="text.secondary">
              {formatDuration(elapsedMs)} decorridos
            </Typography>
          ) : null}

          {displayStatus === "failed" && elapsedMs !== null ? (
            <Typography variant="caption" color="text.secondary">
              Falhou após {formatDuration(elapsedMs)}
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

type TicketSummaryProps = {
  op: Operation;
};

// TicketSummary fica colapsado por default — mostrar UUIDs de 36 chars
// inline polui o card e o shortId de 8 chars não basta pra localizar o
// ticket de fato (e não dá pra copiar). Aberto, lista os UUIDs completos
// com click-to-copy em cada linha (state local por linha, sem coreografia
// cross-row — clipboard já é a fonte da verdade).
function TicketSummary({ op }: TicketSummaryProps) {
  const [open, setOpen] = useState(false);

  const ids =
    op.kind === "single"
      ? (() => {
          const id = op.single?.ticketId ?? op.ticketIds[0];
          return id ? [id] : [];
        })()
      : (op.ticketIds ?? []);

  if (ids.length === 0) return null;

  // "TicketID" alinha com o aria-label do SingleTicketForm e diferencia dos
  // outros IDs visíveis no card (trackingId, _id). Distintivo o bastante
  // pra não colidir com "▼ ver tickets" do BatchDetails (que mostra progresso
  // por ticket, não os IDs em si).
  // Contagem só no estado fechado — quando aberto a lista mostra tudo, sufixo
  // virou redundância visual.
  const noun = op.kind === "single" ? "TicketID" : "TicketIDs";
  const countSuffix = op.kind === "batch" ? ` (${ids.length})` : "";

  return (
    <Stack spacing={0.5}>
      <Button
        size="small"
        variant="text"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ alignSelf: "flex-start", textTransform: "none" }}
      >
        {open ? `ocultar ${noun}` : `ver ${noun}${countSuffix}`}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Stack
          spacing={0.25}
          sx={{
            mt: 0.5,
            p: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          {ids.map((id) => (
            <TicketRow key={id} ticketId={id} />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

// useCopy: copia para o clipboard e marca "copiado" por um instante.
function useCopy(value: string): [boolean, () => Promise<void>] {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setCopied(false), 400);
    } catch {
      // clipboard pode falhar em contextos não-secure; sem feedback o user
      // repete a ação ou copia selecionando o texto.
    }
  };
  return [copied, copy];
}

// TrackingIdCopy: o card mostra 8 chars; o id completo (o que o suporte procura
// nos logs) fica no tooltip e é copiado no clique.
function TrackingIdCopy({ trackingId }: { trackingId: string }) {
  const [copied, copy] = useCopy(trackingId);
  return (
    <Tooltip title={copied ? "Copiado!" : `${trackingId} — clique para copiar`} arrow>
      <Typography
        variant="body2"
        role="button"
        tabIndex={0}
        onClick={copy}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            copy();
          }
        }}
        aria-label={copied ? `Copiado trackingId ${trackingId}` : `Copiar trackingId ${trackingId}`}
        sx={{ fontFamily: "monospace", fontWeight: 600, cursor: "pointer" }}
      >
        {shortId(trackingId)}
      </Typography>
    </Tooltip>
  );
}

type TicketRowProps = {
  ticketId: string;
};

function TicketRow({ ticketId }: TicketRowProps) {
  const [copied, handleClick] = useCopy(ticketId);

  // Linha inteira é clickable pra copiar. Tooltip cobre a row toda. ✓
  // aparece só na confirmação (default sem ícone evita duplicar a tooltip).
  return (
    <Tooltip
      title={copied ? "Copiado!" : "Clique para copiar"}
      arrow
      placement="left"
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        aria-label={copied ? `Copiado ${ticketId}` : `Copiar ${ticketId}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          cursor: "pointer",
          transition: "background-color 0.15s",
          "&:hover": { bgcolor: "action.selected" },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            flex: 1,
            wordBreak: "break-all",
          }}
        >
          {ticketId}
        </Typography>
        {/* Sem ícone de copiar default — tooltip "Clique para copiar" + hover
            bg + cursor pointer já sinalizam interatividade. ✓ aparece só na
            confirmação por 2s pra não duplicar a info. */}
        {copied ? <CheckIcon fontSize="small" color="success" /> : null}
      </Box>
    </Tooltip>
  );
}

type PhaseProgressBarsProps = {
  op: Operation;
  shortTrackingId: string;
};

function PhaseProgressBars({ op, shortTrackingId }: PhaseProgressBarsProps) {
  const phase = op.phase;

  const isPreDownload = phase === "locking" || phase === "metadata" || !phase;

  if (isPreDownload) {
    return (
      <Stack spacing={0.5}>
        <LinearProgress
          variant="indeterminate"
          sx={{ height: 8, borderRadius: 1 }}
          aria-label={`Iniciando operação ${shortTrackingId}`}
        />
        <Typography variant="body2" color="text.secondary">
          {phase ? (PHASE_LABEL[phase] ?? phase) : "Iniciando"}
          {op.msg ? ` · ${op.msg}` : ""}
        </Typography>
      </Stack>
    );
  }

  // As duas barras andam juntas durante a leitura: "Lendo" pelos bytes do
  // stream, "Insert" pelos eventos. Operação antiga (sem bytes, ou fase
  // downloading) mostra a barra sem número em vez de quebrar.
  const progress = op.kind === "single" ? op.single : op.batch;
  const done = phase === "done";
  const active = phase === "downloading" || phase === "scanning" || phase === "finalizing";
  const bytesTotal = progress?.bytesTotal ?? 0;
  const bytesRead = progress?.bytesRead ?? 0;

  const readPct = done ? 100 : bytesTotal > 0 ? ratioPct(bytesRead, bytesTotal) : null;
  const insertPct = done
    ? 100
    : typeof progress?.insertPct === "number"
      ? progress.insertPct
      : null;

  const hours =
    op.kind === "single" && op.single
      ? `horas lidas ${op.single.buckets.current}/${op.single.buckets.total}`
      : op.kind === "batch" && op.batch
        ? `horas lidas ${op.batch.blobs.scanned}/${op.batch.blobs.uniqueTotal}`
        : null;
  const bytes = bytesTotal > 0 ? `${formatBytes(bytesRead)} de ${formatBytes(bytesTotal)}` : null;

  return (
    <Stack spacing={1.5}>
      <PhaseBar
        label="Lendo"
        pct={readPct}
        active={active}
        ariaLabel={`Lendo — operação ${shortTrackingId}`}
        helper={[bytes, hours].filter(Boolean).join(" · ") || null}
      />
      <PhaseBar
        label="Insert"
        pct={insertPct}
        active={active}
        ariaLabel={`Insert — operação ${shortTrackingId}`}
        helper={null}
      />
    </Stack>
  );
}

type PhaseBarProps = {
  label: string;
  pct: number | null;
  active: boolean;
  ariaLabel: string;
  helper: string | null;
};

function PhaseBar({ label, pct, active, ariaLabel, helper }: PhaseBarProps) {
  const hasPct = typeof pct === "number";
  const showIndeterminate = active && !hasPct;
  return (
    <Stack spacing={0.25}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={1}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: active ? "text.primary" : "text.secondary" }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: hasPct ? "text.primary" : "text.disabled",
          }}
        >
          {hasPct ? `${pct}%` : showIndeterminate ? "…" : "—"}
        </Typography>
      </Stack>
      <LinearProgress
        variant={hasPct ? "determinate" : showIndeterminate ? "indeterminate" : "determinate"}
        value={hasPct ? pct : 0}
        sx={{
          height: 8,
          borderRadius: 1,
          opacity: active || hasPct ? 1 : 0.4,
        }}
        aria-label={ariaLabel}
      />
      {helper ? (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </Stack>
  );
}
